import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation, matchPath } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useCampaign } from '../../context/CampaignContext';
import { resolveEntityRoute } from '../../lib/mentions';
import { isGameMaster } from '../../lib/utils';
import { t } from '../../lib/i18n';
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandSeparator,
} from '../ui/command';
import {
  Compass,
  Users,
  MapPin,
  Calendar,
  Book,
  Map,
  Swords,
  Gem,
  Search,
  Plus,
} from 'lucide-react';

const TYPE_CONFIG = {
  quest:     { icon: Compass,  label: { fr: 'Quêtes', en: 'Quests' } },
  npc:       { icon: Users,    label: { fr: 'PNJ', en: 'NPCs' } },
  location:  { icon: MapPin,   label: { fr: 'Lieux', en: 'Locations' } },
  session:   { icon: Calendar, label: { fr: 'Sessions', en: 'Sessions' } },
  lore:      { icon: Book,     label: { fr: 'Encyclopédie', en: 'Encyclopedia' } },
  map:       { icon: Map,      label: { fr: 'Cartes', en: 'Maps' } },
  encounter: { icon: Swords,   label: { fr: 'Rencontres', en: 'Encounters' } },
  treasure:  { icon: Gem,      label: { fr: 'Trésors', en: 'Treasures' } },
};

const QUICK_ACTIONS = [
  { type: 'quest',    path: 'quests',    labelFr: 'Quêtes',       labelEn: 'Quests' },
  { type: 'npc',      path: 'npcs',      labelFr: 'PNJ',          labelEn: 'NPCs' },
  { type: 'location', path: 'locations', labelFr: 'Lieux',        labelEn: 'Locations' },
  { type: 'session',  path: 'sessions',  labelFr: 'Sessions',     labelEn: 'Sessions' },
  { type: 'lore',     path: 'lore',      labelFr: 'Encyclopédie', labelEn: 'Encyclopedia' },
  { type: 'map',      path: 'maps',      labelFr: 'Cartes',       labelEn: 'Maps' },
  { type: 'encounter', path: 'combat',   labelFr: 'Combat',       labelEn: 'Combat', mjOnly: true },
  { type: 'treasure', path: 'treasure',  labelFr: 'Trésors',      labelEn: 'Treasures', mjOnly: true },
];

export const CommandPalette = () => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);

  const { isAuthenticated, user, language } = useAuth();
  const { currentCampaign } = useCampaign();
  const navigate = useNavigate();
  const location = useLocation();

  // Extract campaignId from current URL
  const campaignMatch =
    matchPath({ path: '/campaigns/:campaignId/*' }, location.pathname)
    || matchPath({ path: '/campaigns/:campaignId' }, location.pathname);
  const campaignId = campaignMatch?.params?.campaignId;

  // Check if user is GM
  const userRole = currentCampaign?.members?.find(m => m.user_id === user?.id)?.role;
  const isMJ = isGameMaster(userRole);

  // Global keyboard shortcut: Ctrl+K / Cmd+K
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Expose open function for Navbar button
  useEffect(() => {
    window.__openCommandPalette = () => setOpen(true);
    return () => { delete window.__openCommandPalette; };
  }, []);

  // Debounced search
  const searchEntities = useCallback(async (searchQuery) => {
    if (!campaignId || searchQuery.length < 2) {
      setResults([]);
      return;
    }
    setLoading(true);
    try {
      const response = await axios.get(`/api/campaigns/${campaignId}/search`, {
        params: { q: searchQuery }
      });
      setResults(response.data);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [campaignId]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (query.length < 2) {
      setResults([]);
      return;
    }
    debounceRef.current = setTimeout(() => {
      searchEntities(query);
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, searchEntities]);

  // Reset state when closing
  const handleOpenChange = (isOpen) => {
    setOpen(isOpen);
    if (!isOpen) {
      setQuery('');
      setResults([]);
    }
  };

  // Navigate to result
  const handleSelect = (type, id) => {
    const route = resolveEntityRoute(campaignId, type, id);
    if (route) {
      navigate(route);
    }
    handleOpenChange(false);
  };

  // Navigate to quick action page
  const handleQuickAction = (path) => {
    navigate(`/campaigns/${campaignId}/${path}`);
    handleOpenChange(false);
  };

  // Don't render if not authenticated or no campaign context
  if (!isAuthenticated || !campaignId || !currentCampaign || currentCampaign.id !== campaignId) {
    return null;
  }

  // Group results by type
  const groupedResults = {};
  for (const result of results) {
    if (!groupedResults[result.type]) {
      groupedResults[result.type] = [];
    }
    groupedResults[result.type].push(result);
  }

  const filteredQuickActions = QUICK_ACTIONS.filter(
    action => !action.mjOnly || isMJ
  );

  return (
    <CommandDialog open={open} onOpenChange={handleOpenChange}>
      <CommandInput
        placeholder={t('command_palette_placeholder', language)}
        value={query}
        onValueChange={setQuery}
      />
      <CommandList>
        <CommandEmpty>
          {loading
            ? (language === 'fr' ? 'Recherche...' : 'Searching...')
            : t('command_palette_no_results', language)
          }
        </CommandEmpty>

        {/* Search results grouped by type */}
        {Object.entries(groupedResults).map(([type, items]) => {
          const config = TYPE_CONFIG[type];
          if (!config) return null;
          const Icon = config.icon;
          const groupLabel = config.label[language] || config.label.en;

          return (
            <CommandGroup key={type} heading={groupLabel}>
              {items.map((item) => (
                <CommandItem
                  key={item.id}
                  value={`${item.name} ${item.summary || ''}`}
                  onSelect={() => handleSelect(item.type, item.id)}
                >
                  <Icon className="mr-2 h-4 w-4 text-muted-foreground" />
                  <div className="flex flex-col">
                    <span>{item.name}</span>
                    {item.summary && (
                      <span className="text-xs text-muted-foreground truncate max-w-[300px]">
                        {item.summary}
                      </span>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          );
        })}

        {/* Quick actions when no search query */}
        {query.length < 2 && (
          <>
            <CommandSeparator />
            <CommandGroup heading={t('command_palette_quick_actions', language)}>
              {filteredQuickActions.map((action) => {
                const config = TYPE_CONFIG[action.type];
                const Icon = config?.icon || Search;
                return (
                  <CommandItem
                    key={action.path}
                    value={language === 'fr' ? action.labelFr : action.labelEn}
                    onSelect={() => handleQuickAction(action.path)}
                  >
                    <Icon className="mr-2 h-4 w-4 text-muted-foreground" />
                    <span>{language === 'fr' ? action.labelFr : action.labelEn}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </>
        )}
      </CommandList>
    </CommandDialog>
  );
};

export default CommandPalette;
